import { z } from "zod";
import { safeHttpsUrl } from "../../lib/safeUrl";
import type { PreviewCardProps } from "../../lib/previewCards";
import { clampText, MAX_PREVIEW_ROWS, PreviewEmpty, PreviewFooterLink, PreviewShowingOf } from "./shared";

/**
 * Shaped from observed Notion MCP (mcp.notion.com/mcp) structured output:
 * - `notion-search` → `{results: [{id, title, url, type, highlight,
 *   timestamp}], type: "workspace_search"}`
 * - `notion-fetch` → `{metadata: {type}, title, url, text}` (a single page)
 * Spine: either a `results` array or a top-level page `title`. Row/page
 * fields are all optional and `catch`-tolerant.
 */
export const notionSearchSchema = z
  .object({
    results: z
      .array(
        z.object({
          title: z.string().optional().catch(undefined),
          url: z.string().optional().catch(undefined),
        }),
      )
      .optional()
      .catch(undefined),
    title: z.string().optional().catch(undefined),
    url: z.string().optional().catch(undefined),
  })
  .refine((value) => Array.isArray(value.results) || typeof value.title === "string");

type NotionSearchData = z.infer<typeof notionSearchSchema>;

/** Read-only Notion result list: page title + optional https page link per row. */
export function NotionSearchCard({ data, connectorName }: PreviewCardProps<NotionSearchData>) {
  // notion-search carries a results array; notion-fetch is a single page.
  const rows = (
    data.results ?? (data.title ? [{ title: data.title, url: data.url }] : [])
  ).filter((row): row is { title: string; url?: string } => Boolean(row.title));
  const shown = rows.slice(0, MAX_PREVIEW_ROWS);

  return (
    <div className="flex flex-col">
      {shown.length === 0 ? (
        <PreviewEmpty />
      ) : (
        <ul className="flex flex-col">
          {shown.map((row, index) => {
            const href = safeHttpsUrl(row.url);
            const title = clampText(row.title);
            return (
              <li key={index} className="px-3 py-2">
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block truncate font-sans text-[13px] text-ink underline-offset-2 transition-colors duration-150 ease-house hover:underline"
                  >
                    {title}
                  </a>
                ) : (
                  <span className="block truncate font-sans text-[13px] text-ink">{title}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > shown.length ? (
        <PreviewShowingOf shown={shown.length} total={rows.length} />
      ) : null}
      <PreviewFooterLink href="https://www.notion.so/" connector={connectorName || "Notion"} />
    </div>
  );
}
